import axios from 'axios';
import { ANALYTICS_API_BASE_URL } from '../Config/Config';

/**
 * Start async Excel export
 * @param {string} tenantId - Tenant identifier
 * @param {object} filters - Filter values (dateRange, queueId, etc.)
 * @param {string} token - Authorization token
 * @returns {Promise<object>} Job details {jobId, statusUrl}
 */
export async function startExcelExport(tenantId, filters = {}, token) {
    try {
        const url = `${ANALYTICS_API_BASE_URL}/exportCallsToExcel`;

        const requestBody = {
            tenantId,
            filters
        };


        const response = await axios.post(url, requestBody, {
            headers: {
                'Content-Type': 'application/json',
                'ngrok-skip-browser-warning': 'true',
                ...(token && { 'Authorization': `Bearer ${token}` })
            }
        });

        if (response.status === 202) {
            return {
                success: true,
                jobId: response.data.jobId,
                statusUrl: response.data.statusUrl
            };
        } else {
            return {
                success: false,
                error: 'Unexpected response status'
            };
        }
    } catch (error) {
        console.error('✗ Export start error:', error);
        return {
            success: false,
            error: error.response?.data?.error || error.message
        };
    }
}

/**
 * Check export job status
 * @param {string} jobId - Job identifier
 * @param {string} token - Authorization token
 * @returns {Promise<object>} Job status
 */
export async function checkExportStatus(jobId, token) {
    try {
        const url = `${ANALYTICS_API_BASE_URL}/exportStatus/${jobId}`;

        const response = await axios.get(url, {
            headers: {
                'ngrok-skip-browser-warning': 'true',
                ...(token && { 'Authorization': `Bearer ${token}` })
            }
        });

        return {
            success: true,
            ...response.data
        };
    } catch (error) {
        console.error('✗ Export status check error:', error);
        return {
            success: false,
            error: error.response?.data?.error || error.message
        };
    }
}

/**
 * Poll export status until completed or failed
 * @param {string} jobId - Job identifier
 * @param {string} token - Authorization token
 * @param {function} onProgress - Progress callback (status) => void
 * @returns {Promise<object>} Final status
 */
export async function pollExportStatus(jobId, token, onProgress) {
    const POLL_INTERVAL_MS = 2000; // 2 seconds
    const MAX_ATTEMPTS = 300; // 10 minutes max

    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
        const status = await checkExportStatus(jobId, token);

        if (!status.success) {
            throw new Error(status.error);
        }

        // Call progress callback
        if (onProgress) {
            onProgress(status);
        }

        // Check if completed or failed
        if (status.status === 'completed') {
            return status;
        }

        if (status.status === 'failed') {
            throw new Error(status.error || 'Export failed');
        }

        // Wait before next poll
        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
    }

    throw new Error('Export timeout - please try again');
}
